import Papa from 'papaparse';
import * as XLSX from 'xlsx-js-style';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

export const parseImportFile = async (file) => {
  return new Promise((resolve, reject) => {
    const fileExtension = file.name.split('.').pop().toLowerCase();

    if (fileExtension === 'csv') {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transformHeader: (h) => h.trim(),
        complete: (results) => {
          const data = results.data.map((row, idx) => ({
            ...row,
            _rowNumber: idx + 2 
          }));

          resolve({
            data,
            headers: results.meta.fields || [],
            totalRows: data.length,
            fileName: file.name
          });
        },
        error: (error) => reject(new Error(error.message))
      });
    }

    else if (fileExtension === 'xlsx') {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const raw = new Uint8Array(e.target.result);
          const workbook = XLSX.read(raw, { type: 'array' });
          const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
          const allRows = XLSX.utils.sheet_to_json(firstSheet, { header: 1, raw: false });

          const headers = (allRows[0] || []).map(h => String(h).trim());

          const dataRows = allRows
            .map((row, i) => ({ row, excelRow: i + 1 }))
            .slice(1)
            .filter(({ row }) => {
              const firstCell = String(row[0] ?? '').trim();
              return firstCell && !firstCell.startsWith('#');
            });

          const jsonData = dataRows.map(({ row, excelRow }) => {
            const obj = { _rowNumber: excelRow };
            headers.forEach((h, i) => { obj[h] = row[i] ?? ''; });
            return obj;
          });

          resolve({
            data: jsonData,
            headers,
            totalRows: jsonData.length,
            fileName: file.name
          });
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = (error) => reject(error);
      reader.readAsArrayBuffer(file);
    }

    else {
       reject(new Error('bulkImport.unsupportedFormat'));
    }
  });
};

// Template download — Excel 
export const downloadExcelTemplate = async (config, t) => {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(`${config.moduleName}_template`);

  // ── Headers ──
  const headers = config.templateColumns.map(col => t(col.label));
  worksheet.addRow(headers);

  // ── Header row styling ──
  worksheet.getRow(1).eachCell((cell) => {
    cell.font = { bold: true };
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: 'FFD3D3D3' },
    };
  });

  // ── Sample row ──
  const sampleRow = config.templateColumns.map(col =>
    typeof col.example === 'function' ? col.example() : col.example || ''
  );
  worksheet.addRow(sampleRow);

  // ── Column widths ──
  worksheet.columns = config.templateColumns.map(() => ({ width: 20 }));

  // ── Dropdowns via data validation ──
  config.templateColumns.forEach((col, colIdx) => {
    if (!col.dropdownOptions?.length) return;

    const colLetter = String.fromCharCode(65 + colIdx); // A, B, C...

    for (let row = 2; row <= 1000; row++) {
      worksheet.getCell(`${colLetter}${row}`).dataValidation = {
        type: 'list',
        allowBlank: true,
        formulae: [`"${col.dropdownOptions.join(',')}"`],
        showErrorMessage: true,
        errorTitle: 'Invalid Input',
        error: 'Please select from the list',
      };
    }
  });

  // ── Download ──
  const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    saveAs(blob, `${config.moduleName}_template.xlsx`);
  };

  export const downloadCSVTemplate = (config, t) => {
    const headers   = config.templateColumns.map(col => t(col.label));
    const sampleRow = config.templateColumns.map(col =>
      typeof col.example === 'function' ? col.example() : col.example || ''
    );

    const escape = (val) => `"${String(val).replace(/"/g, '""')}"`;

    const csvContent = [headers, sampleRow]
      .map(row => row.map(escape).join(','))
      .join('\n');

    const blob = new Blob(['\uFEFF' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `${config.moduleName}_template.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };
 
  export const downloadTemplate = async (config, format = 'csv', t) => {
    if (format === 'csv') {
      downloadCSVTemplate(config, t);
    } else if (format === 'xlsx') {
      await downloadExcelTemplate(config, t);
    }
  };